import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import Navbar from '../../../../ReusableBlocks/Navbar';
import PartnerDateInput from '../Components/forms/PartnerDateInput';
import { DEFAULT_PARTNER_FORM_OPTIONS } from '../Constants/partnerFormOptions';
import { PARTNER_PORTAL_NOT_AUTHORIZED, PARTNER_PORTAL_PAE_QUEUE } from '../Constants/partnerRoutes';
import { usePartnerFormOptions } from '../Hooks/usePartnerFormOptions';
import {
  mergePSMActionResult,
  PSM_PROJECT_ACTIONS,
  usePSMProjectActions,
} from '../Hooks/usePSMProjectActions';
import { usePSMProjectDetails } from '../Hooks/usePSMProjectDetails';
import { usePSMProjectUpdate } from '../Hooks/usePSMProjectUpdate';
import { usePartnerRoleAccess } from '../Hooks/usePartnerRoleAccess';
import PartnerLayout from '../Layout/PartnerLayout';
import PageHeader from '../Components/shared/PageHeader';
import PartnerActivityLog from '../Components/shared/PartnerActivityLog';
import { mapPAEProjectToForm } from '../Storage/partnerMappers';
import { buildPSMActivityLog } from '../Utils/partnerActivityUtils';
import { formatPartnerDate } from '../Utils/partnerDateUtils';
import { promptForValidProjectLeadEmail } from '../Utils/partnerEmailPrompt';
import {
  getPartnerApiErrorMessage,
  PartnerApiError,
  showPartnerActionErrorPopup,
  showPartnerProjectUpdatedPopup,
  showPartnerValidationErrorPopup,
} from '../Utils/partnerApiFeedback';
import { shouldPromptForProjectLeadEmailCorrection, validatePAEProjectUpdatePayload } from '../Utils/partnerValidation';
import { getPaeModifyStatusOptions, getPaeQueueStatusBadgeMeta, AT_RISK_STATUS } from '../Utils/partnerProjectStatusUtils';

const resolveProjectId = (project) => (
  project?.id
  ?? project?.projectId
  ?? project?.project_id
  ?? project?._id
  ?? null
);

const PsmProjectField = ({ label, error, fullWidth = false, children }) => (
  <div
    className={[
      'psm-project-field',
      fullWidth ? 'psm-project-field-full' : '',
      error ? 'psm-project-field-has-error' : '',
    ].filter(Boolean).join(' ')}
  >
    <label>{label}</label>
    <div className="psm-project-field-control">
      {children}
      {error ? (
        <span className="psm-project-field-error" role="alert">
          {error}
        </span>
      ) : null}
    </div>
  </div>
);

const PSMProjectDetailsPage = ({
  projectId: projectIdProp,
  inline = false,
  onBackToQueue,
  onProjectUpdated,
}) => {
  const { id: routeProjectId } = useParams();
  const projectId = projectIdProp || routeProjectId;
  const access = usePartnerRoleAccess();
  const { formOptions } = usePartnerFormOptions();
  const { project, loading, error, setProject } = usePSMProjectDetails(projectId);
  const { submitting, runAction } = usePSMProjectActions();
  const { updating, updateProject } = usePSMProjectUpdate();
  const options = formOptions || DEFAULT_PARTNER_FORM_OPTIONS;
  const [form, setForm] = useState(null);
  const [errors, setErrors] = useState({});
  const [internalNote, setInternalNote] = useState('');
  const [actionMessage, setActionMessage] = useState('');

  useEffect(() => {
    if (project) {
      setForm(mapPAEProjectToForm(project));
      setErrors({});
      setActionMessage('');
    }
  }, [project]);

  const activityLog = useMemo(() => buildPSMActivityLog(project), [project]);

  const projectStatusOptions = useMemo(
    () => getPaeModifyStatusOptions(
      options.projectStatus,
      form?.projectStatus || project?.projectStatus,
    ),
    [options.projectStatus, form?.projectStatus, project?.projectStatus],
  );

  const getActionNote = () => internalNote.trim();

  const DEFAULT_ACTION_NOTES = {
    [PSM_PROJECT_ACTIONS.APPROVE]: 'Project approved.',
    [PSM_PROJECT_ACTIONS.UPDATE_STATUS]: 'Project status updated.',
    [PSM_PROJECT_ACTIONS.ADD_NOTE]: '',
  };

  const buildActionPayload = (action, extra = {}) => {
    const userNote = getActionNote();
    return {
      action,
      ...extra,
      note: userNote || DEFAULT_ACTION_NOTES[action] || 'PAE action recorded.',
    };
  };

  const buildOptionalNoteActionPayload = (action, extra = {}) => {
    const userNote = getActionNote();
    return userNote
      ? { action, ...extra, note: userNote }
      : { action, ...extra };
  };

  const performAction = async (payload, successMessage, { onSuccess, suppressErrorPopup = false } = {}) => {
    setActionMessage('');

    try {
      const actionableProjectId = resolveProjectId(project) || projectId;
      if (!actionableProjectId) {
        setActionMessage('Unable to identify project. Please return to queue and try again.');
        return false;
      }

      const result = await runAction(actionableProjectId, payload);
      let mergedProject = null;
      setProject((current) => {
        mergedProject = mergePSMActionResult(current, result, payload);
        return mergedProject;
      });
      if (mergedProject) {
        const mergedProjectId = resolveProjectId(mergedProject) || actionableProjectId;
        setForm(mapPAEProjectToForm(mergedProject));
        onProjectUpdated?.({ ...mergedProject, id: mergedProjectId });
      }
      setActionMessage(successMessage);
      onSuccess?.(result);
      return true;
    } catch (actionError) {
      const message = getPartnerApiErrorMessage(actionError, 'Failed to complete PAE action.');
      setActionMessage(message);

      if (actionError instanceof PartnerApiError && actionError.status === 409) {
        await showPartnerActionErrorPopup(message, { isConflict: true });
        return false;
      }

      if (!suppressErrorPopup) {
        await showPartnerActionErrorPopup(message);
      }

      return false;
    }
  };

  const updateField = (field, value) => {
    setForm((previous) => ({ ...previous, [field]: value }));
    setErrors((previous) => {
      if (!previous[field]) {
        return previous;
      }
      const nextErrors = { ...previous };
      delete nextErrors[field];
      return nextErrors;
    });
    setActionMessage('');
  };

  const onToggleProduct = (product) => {
    const selected = new Set(form.productsInScope);
    if (selected.has(product)) {
      selected.delete(product);
    } else {
      selected.add(product);
    }
    updateField('productsInScope', Array.from(selected));
  };

  const handleSave = async () => {
    if (!form || !project) {
      return;
    }

    let workingForm = { ...form };

    if (shouldPromptForProjectLeadEmailCorrection(workingForm.projectLeadEmail)) {
      const correctedEmail = await promptForValidProjectLeadEmail(workingForm.projectLeadEmail);
      if (!correctedEmail) {
        setErrors({
          projectLeadEmail: 'Enter a valid email with @ and . (e.g. name@company.com)',
        });
        setActionMessage('');
        return;
      }
      workingForm = { ...workingForm, projectLeadEmail: correctedEmail };
      setForm(workingForm);
    }

    const check = validatePAEProjectUpdatePayload(workingForm);

    if (!check.isValid) {
      setErrors(check.errors);
      setActionMessage('Please fix the fields marked in red below.');
      requestAnimationFrame(() => {
        document.querySelector('.psm-project-field-has-error')?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      });
      return;
    }

    setErrors({});
    setActionMessage('');

    const actionableProjectId = resolveProjectId(project) || projectId;
    const statusChanged = workingForm.projectStatus !== project.projectStatus;
    const pendingNote = getActionNote();

    try {
      const updated = await updateProject(actionableProjectId, workingForm);
      let mergedProject = {
        ...project,
        ...(updated?.project || updated),
      };

      if (statusChanged) {
        try {
          const statusPayload = buildActionPayload(
            PSM_PROJECT_ACTIONS.UPDATE_STATUS,
            { projectStatus: workingForm.projectStatus },
          );
          const statusResult = await runAction(actionableProjectId, statusPayload);
          mergedProject = mergePSMActionResult(mergedProject, statusResult, statusPayload);
        } catch (statusError) {
          setProject(mergedProject);
          setForm(mapPAEProjectToForm(mergedProject));
          onProjectUpdated?.({
            ...mergedProject,
            id: resolveProjectId(mergedProject) || actionableProjectId,
          });
          setActionMessage(getPartnerApiErrorMessage(
            statusError,
            'Project details saved, but status update failed.',
          ));
          return;
        }
      }

      if (pendingNote && !statusChanged) {
        try {
          const notePayload = { action: PSM_PROJECT_ACTIONS.ADD_NOTE, note: pendingNote };
          const noteResult = await runAction(actionableProjectId, notePayload);
          mergedProject = mergePSMActionResult(mergedProject, noteResult, notePayload);
          setInternalNote('');
        } catch (noteError) {
          setProject(mergedProject);
          setForm(mapPAEProjectToForm(mergedProject));
          onProjectUpdated?.({
            ...mergedProject,
            id: resolveProjectId(mergedProject) || actionableProjectId,
          });
          setActionMessage(getPartnerApiErrorMessage(
            noteError,
            'Project saved, but the internal note could not be added.',
          ));
          return;
        }
      } else if (pendingNote && statusChanged) {
        setInternalNote('');
      }

      setProject(mergedProject);
      setForm(mapPAEProjectToForm(mergedProject));
      onProjectUpdated?.({ ...mergedProject, id: resolveProjectId(mergedProject) || actionableProjectId });
      await showPartnerProjectUpdatedPopup();

      const savedParts = ['Project saved'];
      if (statusChanged) {
        savedParts.push(`status set to ${mergedProject.projectStatus}`);
      }
      if (pendingNote) {
        savedParts.push('internal note recorded');
      }
      setActionMessage(`${savedParts.join('; ')}.`);
    } catch (saveError) {
      if (saveError instanceof PartnerApiError && Object.keys(saveError.fieldErrors).length > 0) {
        setErrors(saveError.fieldErrors);
        setActionMessage('Please fix the fields marked in red below.');
        await showPartnerValidationErrorPopup({
          title: 'Unable to update project',
          fieldErrors: saveError.fieldErrors,
        });
        return;
      }

      setActionMessage(getPartnerApiErrorMessage(saveError, 'Failed to save project.'));
    }
  };

  if (!access.canPerformPAEOperations) {
    return <Navigate to={PARTNER_PORTAL_NOT_AUTHORIZED} replace />;
  }

  const detailView = (content) => {
    if (inline) {
      return content;
    }

    return (
      <>
        <Navbar activeTab="PARTNER_PORTAL" />
        <PartnerLayout access={access}>
          {content}
        </PartnerLayout>
      </>
    );
  };

  if (loading) {
    return detailView(<p className="partner-loading-text">Loading project details...</p>);
  }

  if (!project || !form) {
    if (inline) {
      return (
        <div className="psm-project-detail">
          <button className="psm-project-detail-back btn btn-link p-0" type="button" onClick={onBackToQueue}>
            Back to PAE Queue
          </button>
          <p className="text-danger mt-3">
            {error || 'Unable to load project details. Please try again from the project queue.'}
          </p>
        </div>
      );
    }

    return detailView(
      <div className="psm-project-detail">
        <Link to={PARTNER_PORTAL_PAE_QUEUE} className="psm-project-detail-back">Back to PAE Queue</Link>
        <p className="text-danger mt-3">
          {error || 'Unable to load project details. Please try again from the project queue.'}
        </p>
      </div>
    );
  }

  const statusMeta = getPaeQueueStatusBadgeMeta(project);
  const productsText = (form.productsInScope || []).join(' + ') || '-';
  const isBusy = submitting || updating;

  const onApprove = () => {
    performAction(
      buildActionPayload(PSM_PROJECT_ACTIONS.APPROVE),
      'Project approved.',
    );
  };

  const onRequestInfo = () => {
    performAction(
      buildOptionalNoteActionPayload(PSM_PROJECT_ACTIONS.REQUEST_INFO),
      'Additional information requested from partner.',
    );
  };

  const onMarkAtRisk = () => {
    performAction(
      buildActionPayload(PSM_PROJECT_ACTIONS.UPDATE_STATUS, {
        projectStatus: AT_RISK_STATUS,
      }),
      'Project marked as at risk.',
    );
  };

  const backControl = onBackToQueue ? (
    <button className="partner-project-detail-back btn btn-link p-0" type="button" onClick={onBackToQueue}>
      Back to PAE Queue
    </button>
  ) : (
    <Link to={PARTNER_PORTAL_PAE_QUEUE} className="partner-project-detail-back">Back to PAE Queue</Link>
  );

  return detailView(
    <div className="psm-project-detail partner-pae-project-detail-page">
      <div className="partner-project-detail-nav">
        {backControl}
      </div>

      {error ? <p className="text-danger">{error}</p> : null}
      {actionMessage ? <p className="partner-form-complete-indicator">{actionMessage}</p> : null}

      <PageHeader
        title={`${form.customerName || project.customerName} — ${form.partnerName || project.partnerName}`}
        description={`${productsText} · ${form.engagementType || '-'} · ${form.deploymentModel || '-'} · Submitted ${formatPartnerDate(project.submittedAt)}`}
        rightContent={(
          <>
            <span className="psm-project-view-pill">PAE View</span>
            <span
              className={`partner-project-hero-status-badge ${statusMeta.className || ''}`.trim()}
              role="status"
            >
              <span className="partner-project-hero-status-badge-label">Queue status</span>
              <span className="partner-project-hero-status-badge-main">
                <span className="partner-project-hero-status-dot" aria-hidden="true" />
                {statusMeta.label}
              </span>
            </span>
          </>
        )}
      />

      <div className="psm-project-layout">
        <div className="psm-project-left psm-project-left-primary">
          <section className="psm-project-card partner-pae-panel">
            <header className="partner-project-panel-header partner-project-panel-header--themed">
              <span className="partner-project-panel-step" aria-hidden="true">1</span>
              <span>Partner and customer</span>
            </header>
            <div className="psm-project-card-body">
              <div className="psm-project-field-grid">
                <PsmProjectField label="Partner" error={errors.partnerName}>
                  <input
                    className={`form-control ${errors.partnerName ? 'is-invalid' : ''}`}
                    value={form.partnerName}
                    onChange={(event) => updateField('partnerName', event.target.value)}
                    disabled={isBusy}
                  />
                </PsmProjectField>
                <PsmProjectField label="Partner company" error={errors.companyName}>
                  <input
                    className={`form-control ${errors.companyName ? 'is-invalid' : ''}`}
                    value={form.companyName}
                    onChange={(event) => updateField('companyName', event.target.value)}
                    disabled={isBusy}
                  />
                </PsmProjectField>
                <PsmProjectField label="CSN" error={errors.csn}>
                  <input
                    className={`form-control ${errors.csn ? 'is-invalid' : ''}`}
                    value={form.csn}
                    onChange={(event) => updateField('csn', event.target.value)}
                    disabled={isBusy}
                  />
                </PsmProjectField>
                <PsmProjectField label="Partner Service">
                  <div className="form-control partner-project-field-readonly">
                    {project.partnerServiceName || '-'}
                  </div>
                </PsmProjectField>
                <PsmProjectField label="Partner email" error={errors.partnerEmail}>
                  <input
                    className={`form-control ${errors.partnerEmail ? 'is-invalid' : ''}`}
                    value={form.partnerEmail}
                    onChange={(event) => updateField('partnerEmail', event.target.value)}
                    disabled={isBusy}
                  />
                </PsmProjectField>
                <PsmProjectField label="Customer" error={errors.customerName}>
                  <input
                    className={`form-control ${errors.customerName ? 'is-invalid' : ''}`}
                    value={form.customerName}
                    onChange={(event) => updateField('customerName', event.target.value)}
                    disabled={isBusy}
                  />
                </PsmProjectField>
                <div className="psm-project-field-grid-lead-pair">
                  <PsmProjectField label="Partner Project lead name" error={errors.projectLeadName}>
                    <input
                      className={`form-control ${errors.projectLeadName ? 'is-invalid' : ''}`}
                      value={form.projectLeadName}
                      onChange={(event) => updateField('projectLeadName', event.target.value)}
                      disabled={isBusy}
                    />
                  </PsmProjectField>
                  <PsmProjectField label="Partner Project lead email" error={errors.projectLeadEmail}>
                    <input
                      className={`form-control ${errors.projectLeadEmail ? 'is-invalid' : ''}`}
                      value={form.projectLeadEmail}
                      onChange={(event) => updateField('projectLeadEmail', event.target.value)}
                      disabled={isBusy}
                    />
                  </PsmProjectField>
                </div>
              </div>
            </div>
          </section>
        </div>

        <section className="psm-project-card partner-pae-panel partner-pae-actions-panel">
          <header className="partner-project-panel-header partner-project-panel-header--themed">
            <span>PAE actions</span>
          </header>
          <div className="psm-project-card-body">
            <div className="psm-project-actions-section partner-pae-action-buttons">
              <button
                type="button"
                className="btn partner-pae-action-btn partner-pae-action-btn--approve"
                onClick={onApprove}
                disabled={isBusy}
              >
                <span className="partner-pae-action-btn__icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" focusable="false">
                    <path
                      d="M9.5 16.2 5.8 12.5l1.4-1.4 2.3 2.3 6.5-6.5 1.4 1.4-7.9 7.9z"
                      fill="currentColor"
                    />
                  </svg>
                </span>
                <span className="partner-pae-action-btn__text">
                  <span className="partner-pae-action-btn__label">Approve project</span>
                </span>
              </button>
              <button
                type="button"
                className="btn partner-pae-action-btn partner-pae-action-btn--info"
                onClick={onRequestInfo}
                disabled={isBusy}
              >
                <span className="partner-pae-action-btn__icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" focusable="false">
                    <path
                      d="M4 4h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H8l-4 4V6a2 2 0 0 1 2-2zm2 4v1.5h12V8H6zm0 3v1.5h8V11H6z"
                      fill="currentColor"
                    />
                  </svg>
                </span>
                <span className="partner-pae-action-btn__text">
                  <span className="partner-pae-action-btn__label">Request more info</span>
                </span>
              </button>
              <button
                type="button"
                className="btn partner-pae-action-btn partner-pae-action-btn--at-risk"
                onClick={onMarkAtRisk}
                disabled={isBusy}
              >
                <span className="partner-pae-action-btn__icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" focusable="false">
                    <path
                      d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"
                      fill="currentColor"
                    />
                  </svg>
                </span>
                <span className="partner-pae-action-btn__text">
                  <span className="partner-pae-action-btn__label">Mark at risk</span>
                </span>
              </button>
            </div>
            <PsmProjectField label="Internal note" fullWidth>
              <textarea
                className="form-control"
                rows={2}
                placeholder="Add PAE note — not visible to partner..."
                value={internalNote}
                onChange={(event) => setInternalNote(event.target.value)}
                disabled={isBusy}
              />
            </PsmProjectField>
          </div>
        </section>

        <div className="psm-project-left-wide">
          <section className="psm-project-card psm-project-internal-card psm-project-card-wide partner-pae-panel">
            <header className="partner-project-panel-header partner-project-panel-header--themed partner-project-panel-header--internal">
              <span className="partner-project-panel-step partner-project-panel-step--internal" aria-hidden="true">2</span>
              <span>Internal details</span>
              <span className="psm-project-internal-badge">PAE only</span>
            </header>
            <div className="psm-project-card-body">
              <div className="psm-project-field-grid psm-project-field-grid-three partner-pae-internal-fields-row">
                <PsmProjectField label="Actual customer name" error={errors.actualCustomerName}>
                  <input
                    className={`form-control ${errors.actualCustomerName ? 'is-invalid' : ''}`}
                    placeholder="Verified customer organization name"
                    value={form.actualCustomerName}
                    onChange={(event) => updateField('actualCustomerName', event.target.value)}
                    disabled={isBusy}
                  />
                </PsmProjectField>
                <PsmProjectField label="Actual customer CSN" error={errors.actualCustomerCsn}>
                  <input
                    className={`form-control ${errors.actualCustomerCsn ? 'is-invalid' : ''}`}
                    placeholder="Verified customer CSN"
                    value={form.actualCustomerCsn}
                    onChange={(event) => updateField('actualCustomerCsn', event.target.value)}
                    disabled={isBusy}
                  />
                </PsmProjectField>
                <PsmProjectField label="Salesforce Opportunity ID" error={errors.salesforceOpportunityId}>
                  <input
                    className={`form-control ${errors.salesforceOpportunityId ? 'is-invalid' : ''}`}
                    placeholder="e.g. 006Hs00000ABcdEFG"
                    value={form.salesforceOpportunityId}
                    onChange={(event) => updateField('salesforceOpportunityId', event.target.value)}
                    disabled={isBusy}
                  />
                </PsmProjectField>
              </div>
            </div>
          </section>

          <section className="psm-project-card psm-project-card-wide partner-pae-panel">
            <header className="partner-project-panel-header partner-project-panel-header--themed">
              <span className="partner-project-panel-step" aria-hidden="true">3</span>
              <span>Project details</span>
            </header>
            <div className="psm-project-card-body">
              <PsmProjectField
                label="Products in scope (select all that apply)"
                error={errors.productsInScope}
                fullWidth
              >
                <div className="partner-products-grid partner-products-grid--detail">
                  {options.productsInScope.map((product) => (
                    <button
                      key={product}
                      type="button"
                      className={`partner-chip ${form.productsInScope.includes(product) ? 'is-selected' : ''}`}
                      onClick={() => onToggleProduct(product)}
                      disabled={isBusy}
                    >
                      {product}
                    </button>
                  ))}
                </div>
              </PsmProjectField>

              <div className="psm-project-field-grid psm-project-field-grid-three">
                <PsmProjectField label="Engagement" error={errors.engagementType}>
                  <select
                    className={`partner-form-select form-select ${errors.engagementType ? 'is-invalid' : ''}`}
                    value={form.engagementType}
                    onChange={(event) => updateField('engagementType', event.target.value)}
                    disabled={isBusy}
                  >
                    <option value="">Select</option>
                    {options.engagementTypes.map((value) => (
                      <option key={value} value={value}>{value}</option>
                    ))}
                  </select>
                </PsmProjectField>
                <PsmProjectField label="Deployment" error={errors.deploymentModel}>
                  <select
                    className={`partner-form-select form-select ${errors.deploymentModel ? 'is-invalid' : ''}`}
                    value={form.deploymentModel}
                    onChange={(event) => updateField('deploymentModel', event.target.value)}
                    disabled={isBusy}
                  >
                    <option value="">Select</option>
                    {options.deploymentModels.map((value) => (
                      <option key={value} value={value}>{value}</option>
                    ))}
                  </select>
                </PsmProjectField>
                <PsmProjectField label="Project status" error={errors.projectStatus}>
                  <select
                    className={`partner-form-select form-select ${errors.projectStatus ? 'is-invalid' : ''}`}
                    value={form.projectStatus}
                    onChange={(event) => updateField('projectStatus', event.target.value)}
                    disabled={isBusy}
                  >
                    {projectStatusOptions.map((status) => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </select>
                </PsmProjectField>
              </div>

              <div
                className={[
                  'partner-project-date-row',
                  'partner-project-date-row-four',
                  'partner-pae-project-date-row',
                  form.completedDate ? 'partner-project-date-row-with-completed' : '',
                ].filter(Boolean).join(' ')}
              >
                <PsmProjectField label="Est. start date" error={errors.estimatedStartDate}>
                  <PartnerDateInput
                    className={`form-control ${errors.estimatedStartDate ? 'is-invalid' : ''}`}
                    value={form.estimatedStartDate}
                    onChange={(nextValue) => updateField('estimatedStartDate', nextValue)}
                    disabled={isBusy}
                  />
                </PsmProjectField>
                <PsmProjectField label="Actual start date" error={errors.actualStartDate}>
                  <PartnerDateInput
                    className={`form-control ${errors.actualStartDate ? 'is-invalid' : ''}`}
                    value={form.actualStartDate}
                    onChange={(nextValue) => updateField('actualStartDate', nextValue)}
                    disabled={isBusy}
                  />
                </PsmProjectField>
                <PsmProjectField label="Est. go-live date" error={errors.estimatedGoliveDate}>
                  <PartnerDateInput
                    className={`form-control ${errors.estimatedGoliveDate ? 'is-invalid' : ''}`}
                    value={form.estimatedGoliveDate}
                    onChange={(nextValue) => updateField('estimatedGoliveDate', nextValue)}
                    disabled={isBusy}
                  />
                </PsmProjectField>
                <PsmProjectField label="Actual go-live date" error={errors.actualGoliveDate}>
                  <PartnerDateInput
                    className={`form-control ${errors.actualGoliveDate ? 'is-invalid' : ''}`}
                    value={form.actualGoliveDate}
                    onChange={(nextValue) => updateField('actualGoliveDate', nextValue)}
                    disabled={isBusy}
                  />
                </PsmProjectField>
                {form.completedDate ? (
                  <PsmProjectField label="Completed date">
                    <div className="form-control partner-project-field-readonly partner-project-completed-date-readonly">
                      {formatPartnerDate(form.completedDate)}
                    </div>
                  </PsmProjectField>
                ) : null}
              </div>

              <div className="partner-project-detail-notes-block">
              <PsmProjectField label="Partner description" error={errors.projectDescription} fullWidth>
                <textarea
                  className={`form-control ${errors.projectDescription ? 'is-invalid' : ''}`}
                  rows={2}
                  value={form.projectDescription}
                  onChange={(event) => updateField('projectDescription', event.target.value)}
                  disabled={isBusy}
                />
              </PsmProjectField>

              <div className="partner-project-fields-grid partner-project-fields-grid--detail partner-project-notes-row">
                <PsmProjectField label="Competitive context">
                  <select
                    className="partner-form-select form-select"
                    value={form.competitiveContext}
                    onChange={(event) => updateField('competitiveContext', event.target.value)}
                    disabled={isBusy}
                  >
                    <option value="">Select if applicable</option>
                    {options.competitiveContexts.map((value) => (
                      <option key={value} value={value}>{value}</option>
                    ))}
                  </select>
                </PsmProjectField>
                <PsmProjectField label="Additional notes">
                  <textarea
                    className="form-control"
                    rows={2}
                    value={form.additionalNotes}
                    onChange={(event) => updateField('additionalNotes', event.target.value)}
                    disabled={isBusy}
                  />
                </PsmProjectField>
              </div>
              </div>
            </div>
          </section>

          <div className="partner-form-submit-bar psm-project-save-bar partner-pae-project-save-bar">
            <span className={`partner-form-complete-indicator ${Object.keys(errors).length > 0 ? 'has-error' : ''}`}>
              {Object.keys(errors).length > 0
                ? 'Please fix the fields marked in red below.'
                : ''}
            </span>
            <div className="partner-submit-actions">
              <button
                className="btn btn-primary"
                type="button"
                disabled={isBusy}
                onClick={handleSave}
              >
                {isBusy ? 'Saving...' : 'Save project details'}
              </button>
            </div>
          </div>

          <section className="psm-project-card partner-pae-panel">
            <header className="partner-project-panel-header partner-project-panel-header--themed">
              <span className="partner-project-panel-step partner-project-panel-step--timeline" aria-hidden="true">4</span>
              <span>Activity log</span>
            </header>
            <div className="psm-project-card-body partner-activity-log-panel">
              <PartnerActivityLog entries={activityLog} />
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export const PSMProjectDetailContent = ({ projectId, onBackToQueue, onProjectUpdated }) => (
  <PSMProjectDetailsPage
    inline
    projectId={projectId}
    onBackToQueue={onBackToQueue}
    onProjectUpdated={onProjectUpdated}
  />
);

export default PSMProjectDetailsPage;
